import {Injectable, Injector} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import {mergeMap, Observable, throwError, throwError as observableThrowError} from 'rxjs';
import {AuthService, AuthToken, AuthStorageStrategy} from '../auth.service';
import * as _ from 'lodash';
import {WmNotificationService} from '../../components/notification/wm-notification.service';
import {AuthInterceptorSkipHeader, BaseUrlSkipHeader} from '../../constants/interceptor-custom-headers.constants';
import {WebmateOptions} from '../../../webmate-options';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {AuthApi} from '@webmate-web-core/shared/api/rest/auth.restapi';

@Injectable()
export class ApiAuthInterceptor implements HttpInterceptor {

    private ignoreRegex: RegExp = new RegExp('/assets/.*svg$|/assets/i18n.*');

    private isRefreshing = false;

    constructor(protected authStorageStrategy: AuthStorageStrategy, private authApi: AuthApi, private authService: AuthService, private webmateOptions: WebmateOptions) {
    }

    private isAuthenticated(): boolean {
        const a = this.authStorageStrategy.getAuthInfo();
        return a !== null && !_.isEmpty(a);

    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url.match(this.ignoreRegex)) {
            return next.handle(request);
        }
        let modRequest = request;
        if (request.headers.has(AuthInterceptorSkipHeader)) {
            // This call should be unauthorized, so we forward it
            const headers = request.headers.delete(AuthInterceptorSkipHeader);
            modRequest = request.clone({headers});
        } else if (this.isAuthenticated()) {
            let authToken = this.authStorageStrategy.getAuthInfo()['access_token'];
            modRequest = request.clone({
                headers: request.headers.set('Authorization', `Bearer ${authToken}`).set('Pragma', 'no-cache').set('Cache-Control', 'no-store')
            });
        }
        if (!request.headers.has(BaseUrlSkipHeader)) {
            let url = this.webmateOptions.versionedApiUri + request.url;
            modRequest = modRequest.clone({url});
        } else {
            const headers = modRequest.headers.delete(BaseUrlSkipHeader);
            modRequest = modRequest.clone({headers});
        }
        return next.handle(modRequest).pipe(catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return this.handle401Error(modRequest, next, error);
            } else {
                return observableThrowError(error);
            }
        }));
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler, origError: HttpErrorResponse): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;

            const authInfo = this.authStorageStrategy.getAuthInfo();
            let refreshToken;
            if (!!authInfo && !!authInfo.refresh_token) {
                refreshToken = authInfo.refresh_token;
            } else {
                return throwError(() => origError);
            }

            return this.authApi.refreshAuthToken(refreshToken).pipe(
                switchMap((newAuthInfo: AuthToken) => {
                    this.isRefreshing = false;

                    this.authStorageStrategy.storeAuthInfo(newAuthInfo, true);

                    let newAccessToken = this.authStorageStrategy.getAuthInfo()['access_token'];
                    let modRequest = request.clone({
                        headers: request.headers.set('Authorization', `Bearer ${newAccessToken}`)
                    });
                    return next.handle(modRequest);
                }),
                catchError((err) => {
                    if (err instanceof HttpErrorResponse && err.status === 403) {
                        // Wait for 3 seconds if another tab successfully provides a new access token,
                        // otherwise navigate to the login screen
                        const timeouthandle = setTimeout(() => {
                            this.authService.logout({preserveReturnUrl: true});
                        }, 3000);
                        return this.authStorageStrategy.observeAuthInfo().pipe(filter(t => t.access_token !== authInfo.access_token), mergeMap((t) => {
                            clearTimeout(timeouthandle);
                            let newAccessToken = t.access_token;
                            let modRequest = request.clone({
                                headers: request.headers.set('Authorization', `Bearer ${newAccessToken}`)
                            });
                            return next.handle(modRequest);
                        }))
                    } else {
                        this.isRefreshing = false;
                        this.authService.logout({preserveReturnUrl: true});
                        return throwError(err);
                    }
                })
            );
        } else {
            // If there are several requests in parallel while a token refresh has already been requested,
            // block the request until the new auth token is available and then repeat it
            const oldToken = this.authStorageStrategy.getAuthInfo().access_token;
            return this.authStorageStrategy.observeAuthInfo().pipe(filter(t => t.access_token !== oldToken), mergeMap((t) => {
                let newAccessToken = t.access_token;
                let modRequest = request.clone({
                    headers: request.headers.set('Authorization', `Bearer ${newAccessToken}`)
                });
                return next.handle(modRequest);
            }));
        }
    }
}
